import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, DollarSign, Users, Music } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const RoyaltySplitCalculator = ({ onCalculate }) => {
  const [totalAmount, setTotalAmount] = useState(10000);
  const [artistPercent, setArtistPercent] = useState(50);
  const [splitResult, setSplitResult] = useState(null);

  useEffect(() => {
    // Auto-calculate on change
    calculateSplit();
  }, [totalAmount, artistPercent]);

  const calculateSplit = async () => {
    const result = await onCalculate(totalAmount, artistPercent);
    if (result) {
      setSplitResult(result);
    } else {
      // Fallback calculation if API fails
      const producerPercent = 100 - artistPercent;
      setSplitResult({
        total_amount: totalAmount,
        artist_percent: artistPercent,
        producer_percent: producerPercent,
        artist_payout: Math.round(totalAmount * (artistPercent / 100) * 100) / 100,
        producer_payout: Math.round(totalAmount * (producerPercent / 100) * 100) / 100,
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div
      className="bg-meta-surface border border-meta-border"
      data-testid="royalty-split-calculator"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-meta-border">
        <div className="flex items-center gap-3">
          <Calculator className="text-meta-neon" size={20} />
          <div>
            <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Royalty Calculator
            </h2>
            <p className="font-sans font-semibold text-meta-text">
              Split Ledger
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Total Amount Input */}
        <div className="space-y-2">
          <label className="text-meta-text-muted font-mono text-xs uppercase flex items-center gap-2">
            <DollarSign size={14} />
            Total Revenue
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-meta-text-muted">
              $
            </span>
            <Input
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(parseFloat(e.target.value) || 0)}
              className="bg-meta-base border-meta-border text-meta-text font-mono text-lg pl-8 focus:ring-meta-neon"
              data-testid="total-amount-input"
            />
          </div>
        </div>

        {/* Split Slider */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-meta-text-muted font-mono text-xs uppercase flex items-center gap-2">
              <Users size={14} />
              Artist / Producer Split
            </label>
            <span className="font-mono text-sm text-meta-neon">
              {artistPercent}% / {100 - artistPercent}%
            </span>
          </div>

          <Slider
            value={[artistPercent]}
            onValueChange={([value]) => setArtistPercent(value)}
            max={100}
            min={0}
            step={1}
            className="w-full"
            data-testid="split-slider"
          />

          {/* Labels */}
          <div className="flex justify-between text-xs font-mono">
            <span className="text-meta-neon">Artist</span>
            <span className="text-meta-warning">Producer</span>
          </div>
        </div>

        {/* Results */}
        {splitResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 gap-4 pt-4 border-t border-meta-border"
          >
            {/* Artist Payout */}
            <div className="bg-meta-neon-dim border border-meta-neon/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Music size={14} className="text-meta-neon" />
                <span className="font-mono text-xs uppercase text-meta-neon">
                  Artist Payout
                </span>
              </div>
              <p
                className="font-mono text-2xl font-bold text-meta-neon tabular-nums"
                data-testid="artist-payout"
              >
                {formatCurrency(splitResult.artist_payout)}
              </p>
              <p className="text-xs text-meta-text-muted mt-1">
                {splitResult.artist_percent}% of total
              </p>
            </div>

            {/* Producer Payout */}
            <div className="bg-yellow-900/20 border border-meta-warning/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Music size={14} className="text-meta-warning" />
                <span className="font-mono text-xs uppercase text-meta-warning">
                  Producer Payout
                </span>
              </div>
              <p
                className="font-mono text-2xl font-bold text-meta-warning tabular-nums"
                data-testid="producer-payout"
              >
                {formatCurrency(splitResult.producer_payout)}
              </p>
              <p className="text-xs text-meta-text-muted mt-1">
                {splitResult.producer_percent}% of total
              </p>
            </div>
          </motion.div>
        )}

        {/* Common Presets */}
        <div className="pt-4 border-t border-meta-border">
          <p className="text-meta-text-muted font-mono text-xs uppercase mb-3">
            Quick Presets
          </p>
          <div className="flex gap-2 flex-wrap">
            {[
              { label: "50/50", value: 50 },
              { label: "60/40", value: 60 },
              { label: "70/30", value: 70 },
              { label: "80/20", value: 80 },
            ].map((preset) => (
              <Button
                key={preset.label}
                variant="outline"
                size="sm"
                onClick={() => setArtistPercent(preset.value)}
                className={`border-meta-border text-xs font-mono ${
                  artistPercent === preset.value
                    ? "border-meta-neon text-meta-neon"
                    : "text-meta-text-secondary hover:border-meta-neon hover:text-meta-neon"
                }`}
                data-testid={`preset-${preset.value}`}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
